import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from '@bibliocommons/table';

import { EMPTY_LIST_URL } from 'app/constants/admin/browse/home';
import { listUrlShape } from 'app/shapes/admin/browse/home';
import ContextMenu from './ContextMenu';
import EditorListUrl from './EditorListUrl';
import ReOrderButtons from './ReOrderButtons';
import TableWrapper from './TableWrapper';

import './EditorListUrls.scss';

const getNumRows = (listUrls, numRowsMin, numRowsMax) => {
  const numListUrls = listUrls.filter(Boolean).length;

  return Math.max(Math.min(numListUrls + 1, numRowsMax), numRowsMin);
};

const EditorListUrls = ({ listUrls, listUrlErrors, numRowsMax, numRowsMin, onChange }) => {
  const numRows = getNumRows(listUrls, numRowsMin, numRowsMax);
  const initialRows = [...Array(numRows)].fill(null).map((_, index) => {
    const listUrl = listUrls[index] || EMPTY_LIST_URL;

    return {
      isEditing: !listUrl.url,
      listUrl
    };
  });

  const [state, setState] = useState({
    rows: initialRows,
    timestampLastSave: Date.now()
  });

  const { rows, timestampLastSave } = state;

  useEffect(() => {
    const newListUrls = rows.map(row => row.listUrl).filter(listUrl => Boolean(listUrl.url));

    onChange(newListUrls);

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [state.timestampLastSave]);

  const canMoveDown = index => {
    return Boolean(rows[index + 1]?.listUrl);
  };

  const handleCancelClick = index => {
    setState(prevState => {
      const { rows: prevRows } = prevState;

      const newRows = [...prevRows];

      newRows[index].isEditing = false;

      return {
        ...prevState,
        rows: newRows
      };
    });
  };

  const handleEditClick = index => {
    setState(prevState => {
      const { rows: prevRows } = prevState;

      const newRows = [...prevRows];

      newRows[index].isEditing = true;

      return {
        ...prevState,
        rows: newRows
      };
    });
  };

  const handleUrlChange = (index, newListUrl) => {
    setState(prevState => {
      const { rows: prevRows } = prevState;

      const newRows = prevRows
        .map((row, nextIndex) => {
          if (nextIndex === index) {
            return {
              isEditing: false,
              listUrl: newListUrl
            };
          }

          return row;
        })
        .filter(row => Boolean(row.listUrl.url));

      const newNumRows = getNumRows(
        newRows.map(row => row.listUrl),
        numRowsMin,
        numRowsMax
      );

      while (newRows.length < newNumRows) {
        newRows.push({
          isEditing: true,
          listUrl: EMPTY_LIST_URL
        });
      }

      return {
        ...prevState,
        rows: newRows,
        timestampLastSave: Date.now()
      };
    });
  };

  const handleUrlDelete = index => {
    setState(prevState => {
      const { rows: prevRows } = prevState;

      const newRows = prevRows.filter((row, nextIndex) => {
        return Boolean(row.listUrl) && nextIndex !== index;
      });

      const newNumRows = getNumRows(
        newRows.map(row => row.listUrl),
        numRowsMin,
        numRowsMax
      );

      while (newRows.length < newNumRows) {
        newRows.push({
          isEditing: true,
          listUrl: EMPTY_LIST_URL
        });
      }

      return {
        ...prevState,
        rows: newRows,
        timestampLastSave: Date.now()
      };
    });
  };

  const handleUrlsReOrder = newRows => {
    setState({
      rows: newRows,
      timestampLastSave: Date.now()
    });
  };

  const instanceId = useRef(uniqueId('EditorListUrls_')).current;

  return (
    <div className="cp-admin-browse-home-editor-list-urls">
      <TableWrapper>
        <Table>
          <TableHeader>
            <TableHeading>List title</TableHeading>
            <TableHeading colSpan={2}>List URL</TableHeading>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const { isEditing, listUrl } = row;

              const { title, url } = listUrl;
              const errorMessage = listUrlErrors?.[index] || '';
              const confirmDeleteModalMessage = `Are you sure you want to delete the '${listUrl}' list URL?`;
              const rowKey = url || `EditorListUrls_${instanceId}_AddUrl_${timestampLastSave}_${index}`;

              return (
                <TableRow key={rowKey}>
                  {isEditing ? (
                    <TableCell colSpan={3}>
                      <EditorListUrl
                        errorMessage={errorMessage}
                        listUrl={listUrl}
                        onCancel={() => handleCancelClick(index)}
                        onChange={newListUrl => {
                          handleUrlChange(index, newListUrl);
                        }}
                      />
                    </TableCell>
                  ) : (
                    <>
                      <TableCell>{title}</TableCell>
                      <TableCell>{url}</TableCell>
                      <TableCell className="cell-actions">
                        <div className="actions-wrapper">
                          <ReOrderButtons
                            canMoveDown={canMoveDown}
                            index={index}
                            items={rows}
                            onChange={handleUrlsReOrder}
                          />
                          <ContextMenu
                            className="action-context-menu"
                            confirmDeleteModalHeading="Delete list URL"
                            confirmDeleteModalMessage={confirmDeleteModalMessage}
                            onDeleteClick={() => handleUrlDelete(index)}
                            onEditClick={() => handleEditClick(index)}
                          />
                        </div>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableWrapper>
    </div>
  );
};

EditorListUrls.propTypes = {
  listUrls: PropTypes.arrayOf(listUrlShape).isRequired,
  listUrlErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
  numRowsMax: PropTypes.number.isRequired,
  numRowsMin: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired
};

export default EditorListUrls;
